import { ELLCommerce, Order } from "@pearson-ell/commerce-sdk";
import React, { useEffect, useRef } from "react";
import { formatPrice } from "../../commons/utils";
import { useTranslation } from "react-i18next";
import { useBreakpoints } from "../../commons/hooks";
import {
    CartOrderSummaryComponentBreakPoints,
    StyledCartOrderDiscount,
    StyledOrderHeader,
    StyledCartOrderPrice,
    StyledCartOrderPriceWrapper,
    StyledCartOrderSubtotal,
    StyledCartOrderSummary,
    StyledCartOrderTotalWrapper,
    StyledTotalLabel,
} from "../CartOrderSummary/CartOrderSummary.parts";
import { PrintIcon } from "../../commons/svgs";

export interface IOrderSummary {
    className?: string;
    order: Order;
    printCallback?: (
        e:
            | React.MouseEvent<HTMLDivElement>
            | React.KeyboardEvent<HTMLDivElement>
    ) => void;
}

export const OrderSummary: React.FC<IOrderSummary> = ({
    className,
    order,
    printCallback,
}: IOrderSummary) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    useEffect(() => {
        return () => {
            ELLCommerce.GTagService.pushEvent({ action: "purchase", order: order });
        };
    }, []);

    const breakpoint = useBreakpoints<CartOrderSummaryComponentBreakPoints>(
        containerRef,
        [
            CartOrderSummaryComponentBreakPoints.mobileSm,
            CartOrderSummaryComponentBreakPoints.mobileMd,
            CartOrderSummaryComponentBreakPoints.desktopSm,
        ]
    );

    const onPrint = (
        ev:
            | React.MouseEvent<HTMLDivElement>
            | React.KeyboardEvent<HTMLDivElement>
    ) => {
        ev.preventDefault();
        if (printCallback) {
            printCallback(ev);
        }
    };

    if (!order) {
        return <div></div>;
    }

    return (
        <StyledCartOrderSummary
            className={className}
            ref={containerRef}
            breakpoint={breakpoint}
        >
            <div className="order-summary-block" data-testid="order-summary-block">
                <StyledOrderHeader data-testid="OrderHeader">
                    {t("order_summary")}
                    {printCallback && (
                        <div className="print-container" onClick={(ev) => onPrint(ev)}>
                            <PrintIcon
                                aria-hidden={true}
                            />
                            <span>Print</span>
                        </div>
                    )}
                </StyledOrderHeader>
                <StyledCartOrderPriceWrapper data-testid="CartOrderPriceWrapper">
                    {order.products.map((p) => (
                        <StyledCartOrderDiscount key={p.sku} data-testid="CartOrderDiscount">
                            <span>{p.name}</span>
                            <StyledCartOrderPrice data-testid={`CartOrderPrice-${p.sku}`}>
                                {formatPrice(p.totalIncludingTax, order.currency)}
                            </StyledCartOrderPrice>
                        </StyledCartOrderDiscount>
                    ))}
                    <br></br>
                </StyledCartOrderPriceWrapper>
                <StyledCartOrderPriceWrapper data-testid="CartOrderPriceWrapper">
                    <StyledCartOrderSubtotal data-testid="CartOrderSubtotal">
                        <span>{t("subtotal")}</span>
                        <StyledCartOrderPrice data-testid="CartOrderSubtotalPrice">
                            {formatPrice(order.subtotal, order.currency)}
                        </StyledCartOrderPrice>
                    </StyledCartOrderSubtotal>
                    {order.discountTotal > 0 && (
                        <StyledCartOrderDiscount data-testid="CartOrderDiscount">
                            <span>{t("discount")}</span>
                            <StyledCartOrderPrice data-testid="CartOrderDiscountPrice">
                                -
                                {formatPrice(order.discountTotal, order.currency)}
                            </StyledCartOrderPrice>
                        </StyledCartOrderDiscount>
                    )}
                    {order.taxTotal > 0 && (
                        <StyledCartOrderDiscount data-testid="CartOrderDiscount">
                            <span>{t("tax")}</span>
                            <StyledCartOrderPrice data-testid="CartOrderTaxPrice">
                                {formatPrice(order.taxTotal, order.currency)}
                            </StyledCartOrderPrice>
                        </StyledCartOrderDiscount>
                    )}
                    <br></br>
                </StyledCartOrderPriceWrapper>
                <StyledCartOrderTotalWrapper data-testid="CartOrderTotalWrapper">
                    <StyledTotalLabel data-testid="CartOrderTotalLabel">
                        <span className="label">
                            {t("total")}{" "}
                            {order.currency.isoCode ? `(${order.currency?.isoCode})` : null}
                        </span>
                    </StyledTotalLabel>
                    <StyledCartOrderPrice data-testid="CartOrderTotalPrice">
                        {formatPrice(order.total, order.currency)}
                    </StyledCartOrderPrice>
                </StyledCartOrderTotalWrapper>
            </div>
        </StyledCartOrderSummary>
    );
};
